import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import Grid from '@mui/material/Grid'
import { AdCreativeUpdate, AdCreativeWithUrls, Role } from 'common/api/v1/types'
import RHF, { FormProps } from '../../../common/Form/RHF'
import { AppDispatch, GlobalState, useRoutes } from '../../../../store'
import Wrapper from '../../../common/Wrapper'
import Pendable from '../../../common/Pendable'
import { formTransform, useConfirmationDialog, useUser } from '../../../../utils'
import {
  clearAdCreative,
  deleteAdCreative,
  getAdCreative,
  updateAdCreative,
} from '../../../../redux/actions/adCreativeActions'
import { ButtonsPane, Form, Paper, SafeRouting, TextInput } from '../../../common/Form'
import { equals } from 'common/util'
import { Metadata } from './Metadata'
import { AdPodTable } from './AdPodTable'
import { AdBreakTable } from './AdBreakTable'
import Stack from '@mui/material/Stack'

type FormState = AdCreativeUpdate & {
  adCreative?: AdCreativeWithUrls
}

const getInitialState = (adCreative?: AdCreativeWithUrls): FormState => ({
  id: adCreative?.id || '',
  name: adCreative?.name,
  durationMs: adCreative?.userDurationMs || ('' as any),
  adCreative: adCreative,
})

const AdCreativeForm = ({ getValues, setError, setFocus, formState }: FormProps<FormState>) => {
  const values = getValues()
  const routes = useRoutes()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const setConfirmation = useConfirmationDialog()
  const user = useUser()

  const { formErrors, saving } = useSelector(
    ({ adCreativeReducer }: GlobalState) => ({
      formErrors: adCreativeReducer.formErrors,
      saving: adCreativeReducer.saving,
    }),
    shallowEqual,
  )

  useEffect(() => {
    if (Array.isArray(formErrors)) {
      formErrors.forEach((item) => {
        setError(item.name as keyof FormState, { type: 'manual', message: item.reason })
      })
      if (formErrors[0]) setFocus(formErrors[0].name as keyof FormState)
    }
  }, [formErrors, setError, setFocus])

  if (
    values.adCreative &&
    user.role !== Role.super &&
    (user.role !== Role.admin || user.group !== values.adCreative.groupId)
  ) {
    navigate(-1)
    return null
  }

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <SafeRouting formState={formState} />
        <Form id="ad-creative-form" noValidate>
          <Paper>
            <TextInput name="name" label="Name" required autoFocus />
            <TextInput
              name="durationMs"
              label="Duration (ms)"
              tooltip="Override the probed duration"
              type="number"
              validators={{
                number: {
                  greaterThanOrEqualTo: 0,
                  message: `Must be greater than or equal to 0`,
                },
              }}
            />
          </Paper>

          {values.adCreative && (
            <>
              <Metadata adCreative={values.adCreative} sx={{ mt: 2 }} />
              <Stack direction={'row'} spacing={2} sx={{ mt: 2 }}>
                <AdPodTable adCreative={values.adCreative} />
                <AdBreakTable adCreative={values.adCreative} />
              </Stack>
            </>
          )}

          <ButtonsPane
            main={{
              Cancel: {
                onClick: () => navigate(routes.adCreatives()),
              },
              Save: { savingState: !!saving, primary: true, type: 'submit' },
            }}
            secondary={
              user.role === Role.super
                ? {
                    Delete: {
                      onClick: () =>
                        setConfirmation(
                          () => {
                            dispatch(deleteAdCreative(values.id))
                            navigate(routes.adCreatives())
                          },
                          'Are you sure you want to delete this Ad Creative?',
                          values.name,
                        ),
                    },
                  }
                : undefined
            }
          />
        </Form>
      </Grid>
    </Grid>
  )
}

export const EditAdCreative = () => {
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    id && dispatch(getAdCreative(id))
    return () => {
      dispatch(clearAdCreative())
    }
  }, [])

  const selectedAdCreative = useSelector(
    ({ adCreativeReducer }: GlobalState) => adCreativeReducer.selectedAdCreative,
    equals,
  )

  return (
    <Wrapper name={['Ad Insertion', 'Ad Creatives', selectedAdCreative?.name]}>
      <Pendable pending={!!id && !selectedAdCreative}>
        <RHF
          onSubmit={(values) => {
            const { adCreative, ...update } = values
            if ((update.durationMs as any) === '') {
              update.durationMs = -1
            }
            dispatch(updateAdCreative(formTransform(update)))
          }}
          defaultValues={getInitialState(selectedAdCreative)}
          component={AdCreativeForm}
        />
      </Pendable>
    </Wrapper>
  )
}
